/**
 * ``useAlertsSseBridge`` — merges the live ``alert`` / ``alert-dismissed``
 * SSE deltas the Map already receives into the shared
 * :func:`alerts/alerts-store` buffer (REQ-QUERY-025; ADR-029). The Alert
 * sibling of :func:`detections/use-detections-sse-bridge` under the
 * ADR-032 / ADR-029 cascade.
 *
 * The BFF's SSE stream carries ``status`` / ``detection`` / ``alert`` /
 * ``alert-dismissed`` messages; this bridge handles the last two:
 *
 *   - **``alert``** — a newly-raised alert; upsert into the buffer so it
 *     appears in the Alerts panel (and, PR5b, the Map pin layer) ahead of
 *     the next REST poll (:func:`alerts/use-recent-alerts-feed`).
 *   - **``alert-dismissed``** — the cross-session dismissal fan-out
 *     (ADR-029 [MVP] item 6): this envelope reaches EVERY connected
 *     session whenever any operator dismisses an alert, so the bridge
 *     marks the alert dismissed in the shared store and it drops from the
 *     active panel everywhere at once. Idempotent with the dismiss
 *     mutation's own optimistic ``dismiss`` call (see
 *     :func:`alerts/alerts-store` §"Dismissal semantics").
 *
 * Shaped as a prop-driven hook (taking the connection's
 * :type:`SseSubscribe` rather than reading the SSE context) so it composes
 * with the Map's plumbing — the same value threaded into the detection
 * bridge — and so it unit-tests without a provider: a test passes a
 * two-line fake ``subscribe`` and drives messages directly.
 *
 * # Burst delivery (issue #739)
 *
 * Every alert on the stream reaches this bridge, including several arriving
 * in one ``text/event-stream`` flush. This hook previously read a single
 * ``lastMessage`` slot, and this docstring recorded the resulting
 * coalescing as accepted behaviour on the grounds that
 * :func:`alerts/use-recent-alerts-feed`'s poll backfills the store. That
 * reasoning held for the store and *only* for the store: the Flow-F
 * notification surface built on the same feeder
 * (:func:`alerts/use-alert-notifications`) has no backfill, so a coalesced
 * alert was an operator interruption that never happened. The feeder is now
 * a subscription and drops nothing; the REST poll remains the
 * authoritative-metadata backfill, not a loss-recovery mechanism.
 *
 * Cross-references:
 *   - REQ-UI-062 (Alerts panel); REQ-UI-006 (live update latency).
 *   - REQ-QUERY-025 (Alert read + dismiss surface); REQ-INGEST-044
 *     (nullable position — a null-position alert is valid, not dropped).
 *   - ADR-024 §5; ADR-029 §5 (Flow-F surface) / §6 / §10 (alert-flood cap) /
 *     [MVP] item 6 (cross-session dismissal fan-out); ADR-032; Flow F.
 *   - map/sse-subscription.ts (the SseSubscribe fan-out contract).
 */
import type { SseAlertPayload } from "@/map/sse-types";
import { useSseMessages, type SseSubscribe } from "@/map/sse-subscription";
import { type AlertRecord, useAlertsStore } from "@/alerts/alerts-store";

/**
 * Map a live :type:`SseAlertPayload` to the store's :type:`AlertRecord`.
 *
 * The live NATS feed is a subset of the full ``AlertItem``: it lacks the
 * ingest-side metadata columns (``source_aggregator_id`` /
 * ``ingress_timestamp`` / ``message_hash``) and the dismissal columns (an
 * alert arriving live is by definition not-yet-dismissed). Those are
 * filled with empty / null defaults here — the panel row contract
 * (priority / type / description / source / age) doesn't read the
 * ingest-metadata, and the authoritative values arrive on the next REST
 * poll, whose upsert overwrites this record by ``alert_id`` (the
 * metadata-bearing REST row wins last-writer). ``ingress_timestamp``
 * falls back to ``received_at`` so the field is never an empty/invalid
 * date; the three dismissal columns are ``null`` (active).
 */
export function alertRecordFromSse(payload: SseAlertPayload): AlertRecord {
  return {
    alert_id: payload.alert_id,
    producer_id: payload.producer_id,
    aggregator_id: payload.aggregator_id,
    received_at: payload.received_at,
    alert_type: payload.alert_type,
    status: payload.status,
    priority: payload.priority,
    description: payload.description,
    region_id: payload.region_id,
    ranking: payload.ranking,
    confidence: payload.confidence,
    latitude_deg: payload.latitude_deg,
    longitude_deg: payload.longitude_deg,
    elevation_m: payload.elevation_m,
    schema_version: payload.schema_version,
    // An alert arriving live is active — dismissal columns are null.
    dismissed_at: null,
    dismissed_by: null,
    dismissal_note: null,
    // Not on the live feed — backfilled by the REST poll (see docstring).
    source_aggregator_id: "",
    ingress_timestamp: payload.received_at,
    message_hash: "",
    source_network: payload.source_network ?? null,
  };
}

/**
 * Merge every arriving ``alert`` / ``alert-dismissed`` SSE message into the
 * alerts store.
 *
 * No-op for every other message kind (``status`` / ``detection``). Fires
 * once per message, including each message of a multi-frame flush. A
 * null-position alert (REQ-INGEST-044) is upserted normally — it is a
 * valid row.
 *
 * @param subscribe - The Map's ``connection.subscribe``. ``null`` mounts no
 *     subscription (no connection yet).
 */
export function useAlertsSseBridge(subscribe: SseSubscribe | null): void {
  useSseMessages(subscribe, (message) => {
    if (message.kind === "alert") {
      useAlertsStore.getState().upsert([alertRecordFromSse(message.payload)]);

      // The Flow-F notification for this alert is raised by
      // :func:`alerts/use-alert-notifications`, mounted alongside this
      // bridge in :func:`map/use-map-sse-bridges`. It lives there rather
      // than here because raising a notification needs React context (the
      // dismiss mutation, the session role, the selection bus) that would
      // cost this pure store-warmer its provider-free testability.
      //
      // (This replaced the PR5a inc 3 ``TODO(flow-f)`` deferral hook, which
      // computed the surface descriptor and deliberately raised nothing
      // because no toast/banner host existed yet — #726.)
    } else if (message.kind === "alert-dismissed") {
      // Cross-session dismissal fan-out (ADR-029 [MVP] item 6): drop the
      // alert from the active view on EVERY session. Idempotent with the
      // dismiss mutation's optimistic call.
      const { alert_id, dismissed_at, dismissed_by } = message.payload;
      useAlertsStore.getState().dismiss(alert_id, {
        dismissedAt: dismissed_at,
        dismissedBy: dismissed_by,
      });
    }
  });
}
